import { User } from "oidc-client-ts";

import { decodeJwtPayload } from "./decodeJwtPayload";

interface RealmRoleClaims {
  realm_access?: {
    roles?: string[];
  };
  realm_roles?: string[];
}

/**
 * The user's Keycloak realm roles, merged from every place they can arrive: the standard
 * `realm_access.roles` (the `roles` client scope) and the flat custom `realm_roles` claim (the
 * realm-roles protocol mapper), read from both the access token and the ID-token profile. Which
 * combination actually carries the roles depends on per-client Keycloak configuration — reading
 * all of them means a single mapper or scope misconfiguration degrades to redundancy instead of
 * an empty role set, which would turn into a full lockout at the apps' role guards.
 */
export function getUserRoles(user: User | null | undefined): string[] {
  // No access token means no role, whatever the profile claims: the role guards decide whether
  // the app is usable, and without a bearer every API call would 401 into a broken shell.
  if (!user?.access_token) {
    return [];
  }

  const accessToken = decodeJwtPayload(user.access_token) as RealmRoleClaims | null;
  const profile = user.profile as RealmRoleClaims | undefined;

  const roles = new Set<string>();
  for (const source of [accessToken, profile]) {
    for (const role of source?.realm_access?.roles ?? []) {
      roles.add(role);
    }
    for (const role of source?.realm_roles ?? []) {
      roles.add(role);
    }
  }

  return [...roles];
}
